import React, { useState } from 'react';

// Styles & Assets
import styles from './DuplicatePreconfigButton.module.css';
import { IoDuplicate } from 'react-icons/io5';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import useHttp from '../../../hooks/useHttp';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';

// Components
import Modal from '../../UI/Modal/Modal';
import DialogDuplicateDelete from '../DialogDuplicateDelete/DialogDuplicateDelete';

// Interfaces
import { Preconfiguration } from '../../../interfaces/Preconfigurations';
import PreconfigActionButton from '../PreconfigActionButton/PreconfigActionButton';

interface Props {
    preconfiguration: Preconfiguration
    addPreconfig: (id: Preconfiguration) => void
}

const DuplicatePreconfigButton: React.FC<Props> = ({ preconfiguration, addPreconfig }) => {
    const { t } = useTranslation();

    const { sendRequest } = useHttp<Pick<Preconfiguration, 'id' | 'name'>>();
    const toast = useToast();

    const [showDuplicatePreconfig, setShowDuplicatePreconfig] = useState(false);

    const handleClickDuplicate = (evt: React.FormEvent) => {
        evt.stopPropagation();
        setShowDuplicatePreconfig(true);
    };

    const handleDuplicate = () => {
        const newPreconfiguration = {
            name: `${preconfiguration.name}_${t('copy')}`,
            profile_ids: preconfiguration.profiles?.map(p => p.id) ?? [],
            pathology_ids: preconfiguration.pathologies?.map(p => p.id) ?? [],
            preconfig_inquiries: preconfiguration.preconfig_inquiries.map(pI => {
                const newPI = { ...pI };
                delete newPI.inquiry;
                delete newPI.id;
                return newPI;
            }),
            preconfig_sheet_parameters: preconfiguration.preconfig_sheet_parameters.map(p => {
                const newParameter = { ...p };
                delete newParameter.id;
                delete newParameter.sheet_parameter;
                return newParameter;
            }),
        };

        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig',
            method: 'POST',
            body: newPreconfiguration
        }, (data) => {
            const preconfig: Preconfiguration = {
                ...preconfiguration,
                ...data,
                name: data.name ?? newPreconfiguration.name,
            };
            addPreconfig(preconfig);
        }, () => {
            toast.error(t('error_duplicating_preconfiguration'));
        });

        setShowDuplicatePreconfig(false);
    };

    return (
        <>
            <PreconfigActionButton type='button' onClick={handleClickDuplicate}>
                <IoDuplicate />
            </PreconfigActionButton>
            <Modal show={showDuplicatePreconfig} setShowModal={setShowDuplicatePreconfig}>
                <DialogDuplicateDelete
                    onCancel={() => setShowDuplicatePreconfig(false)}
                    onConfirm={handleDuplicate}
                    title={t('sure_duplicate_preconfig')}
                />
            </Modal>
        </>
    );
};

export default DuplicatePreconfigButton;